// ═══════════════════════════════════════════════════════════════
// HelpCenter.jsx — Entry point and layout shell
// Tabs: FAQ | Chat | Tickets — plus agent dashboard for support users
// Drop-in: host app provides user, config, adapters
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { mergeConfig, PATHS } from './HelpCenterConfig.js';
import PresenceManager from './PresenceManager.js';
import FAQPanel from './FAQPanel.jsx';
import ChatPanel from './ChatPanel.jsx';
import TicketPanel from './TicketPanel.jsx';
import ChatAgentPanel from './ChatAgentPanel.jsx';
import { resolveRecordScope, ownerQuery, agentQuery, subscribeRecords } from './recordScope.js';

/**
 * <HelpCenter
 *   user={{ id, name, email, role }}
 *   config={{ appName, faqs, primaryColor, ... }}
 *   storageAdapter={firebaseAdapter}
 *   pushAdapter={pushAdapter}
 *   context={{ conflictId: '...' }}        // optional — attached to chats/tickets
 *   agentRoles={['support', 'admin']}      // roles that see the agent dashboard
 *   defaultTab="faq"                       // 'faq' | 'chat' | 'tickets'
 *   floating={true}                        // render as floating widget vs inline
 *   onClose={() => {}}                     // callback when floating widget is closed
 * />
 */
function HelpCenter({
  user,
  config: userConfig,
  storageAdapter,
  pushAdapter,
  context,
  agentRoles = ['support', 'admin'],
  defaultTab = 'faq',
  floating = false,
  onClose,
}) {
  const config = useMemo(() => mergeConfig(userConfig), [userConfig]);
  const [activeTab, setActiveTab] = useState(defaultTab);
  const [ticketInitialMessage, setTicketInitialMessage] = useState(null);
  const [isOpen, setIsOpen] = useState(!floating); // floating starts closed
  const [unreadCount, setUnreadCount] = useState(0);

  // Determine if user is a support agent
  const isAgent = user?.role && agentRoles.includes(user.role);

  // Create presence manager (stable across renders)
  const presenceManager = useMemo(() => {
    if (!storageAdapter) return null;
    return new PresenceManager(storageAdapter, {
      heartbeatIntervalMs: config.heartbeatIntervalMs,
      presenceTimeoutMs: config.presenceTimeoutMs,
    });
  }, [storageAdapter, config.heartbeatIntervalMs, config.presenceTimeoutMs]);

  // Cleanup presence on unmount
  useEffect(() => {
    return () => {
      if (presenceManager && user && isAgent) {
        presenceManager.destroy(user);
      }
    };
  }, [presenceManager, user?.id]);

  // Track actionable items for floating widget badge
  const badgeChatCount = React.useRef(0);
  const badgeTicketCount = React.useRef(0);
  useEffect(() => {
    if (!storageAdapter || !user?.id || !floating) return;
    badgeChatCount.current = 0;
    badgeTicketCount.current = 0;

    const updateBadge = () => setUnreadCount(badgeChatCount.current + badgeTicketCount.current);

    // Subscribe only to what the rules let this user read (see recordScope.js)
    let cancelled = false;
    let unsubChats = () => {};
    let unsubTickets = () => {};
    resolveRecordScope(storageAdapter, user).then((scope) => {
      if (cancelled) return;
      const query = isAgent ? agentQuery(scope) : ownerQuery(scope);

      unsubChats = subscribeRecords(storageAdapter, PATHS.chatSessions(), query, (data) => {
        if (!data) { badgeChatCount.current = 0; updateBadge(); return; }
        const all = Object.values(data);
        badgeChatCount.current = isAgent
          ? all.filter(c => c.status === 'waiting').length
          : all.filter(c => c.userId === user.id && (c.status === 'waiting' || c.status === 'active')).length;
        updateBadge();
      });

      unsubTickets = subscribeRecords(storageAdapter, PATHS.tickets(), query, (data) => {
        if (!data) { badgeTicketCount.current = 0; updateBadge(); return; }
        const all = Object.values(data);
        badgeTicketCount.current = isAgent
          ? all.filter(t => t.status === 'open').length
          : all.filter(t => t.userId === user.id && t.status !== 'resolved').length;
        updateBadge();
      });
    });

    return () => { cancelled = true; unsubChats(); unsubTickets(); };
  }, [storageAdapter, user?.id, floating, isAgent]);

  // Handle chat offline redirect → ticket
  const handleCreateTicket = useCallback((initialMessage) => {
    setTicketInitialMessage(initialMessage);
    setActiveTab('tickets');
  }, []);

  // Handle FAQ "Contact Support" click
  const handleContactSupport = useCallback(() => {
    if (config.chatEnabled) {
      setActiveTab('chat');
    } else if (config.ticketsEnabled) {
      setActiveTab('tickets');
    }
  }, [config.chatEnabled, config.ticketsEnabled]);

  // Toggle floating widget
  const toggleOpen = () => {
    setIsOpen(prev => !prev);
    if (!isOpen) setUnreadCount(0); // clear badge when opening
  };

  const handleClose = () => {
    if (floating) {
      setIsOpen(false);
      if (onClose) onClose();
    }
  };

  // Build available tabs
  const tabs = [];
  if (config.faqEnabled && config.faqs?.length > 0) tabs.push({ id: 'faq', label: 'FAQ', icon: QuestionIcon });
  if (config.chatEnabled) tabs.push({ id: 'chat', label: 'Chat', icon: ChatBubbleIcon });
  if (config.ticketsEnabled) tabs.push({ id: 'tickets', label: 'Tickets', icon: TicketIcon });
  if (isAgent) tabs.push({ id: 'agent', label: 'Dashboard', icon: DashboardIcon });

  // Auto-select first available tab if current is disabled
  useEffect(() => {
    if (tabs.length > 0 && !tabs.find(t => t.id === activeTab)) {
      setActiveTab(tabs[0].id);
    }
  }, [tabs.length]);

  const primaryColor = config.primaryColor || '#d97706';

  // ── Content (shared between inline and floating) ──
  const content = (
    <div className={`flex flex-col bg-white ${floating ? 'rounded-2xl shadow-2xl border border-stone-200' : ''}`}
      style={floating ? { width: '400px', maxHeight: '600px', height: '600px' } : { minHeight: '400px' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-stone-200 flex-shrink-0 rounded-t-2xl" style={{ backgroundColor: primaryColor }}>
        <div className="flex items-center gap-3">
          {config.logoUrl && (
            <img src={config.logoUrl} alt="" className="w-8 h-8 rounded-lg object-cover" />
          )}
          <div>
            <h2 className="text-white font-semibold text-base">{config.appName || 'Help Center'}</h2>
            {isAgent && activeTab === 'agent' && (
              <p className="text-white/70 text-xs">Agent Dashboard</p>
            )}
          </div>
        </div>
        {floating && (
          <button onClick={handleClose} className="p-1 hover:bg-white/20 rounded-lg transition-colors" aria-label="Close help center">
            <svg aria-hidden="true" className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M6 18 18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}
      </div>

      {/* Tab Bar */}
      {tabs.length > 1 && (
        <div className="flex border-b border-stone-200 flex-shrink-0">
          {tabs.map(tab => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => {
                  setActiveTab(tab.id);
                  if (tab.id !== 'tickets') setTicketInitialMessage(null);
                }}
                className={`flex-1 flex items-center justify-center gap-1.5 px-2 py-2.5 text-xs font-medium transition-colors ${
                  isActive
                    ? 'text-stone-800 border-b-2'
                    : 'text-stone-500 hover:text-stone-600'
                }`}
                style={isActive ? { borderBottomColor: primaryColor } : undefined}
              >
                <Icon active={isActive} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto p-4">
        {activeTab === 'faq' && (
          <FAQPanel
            faqs={config.faqs || []}
            storage={storageAdapter}
            user={user}
            primaryColor={primaryColor}
            onContactSupport={handleContactSupport}
          />
        )}
        {activeTab === 'chat' && (
          <ChatPanel
            storage={storageAdapter}
            pushAdapter={pushAdapter}
            presenceManager={presenceManager}
            user={user}
            context={context}
            config={config}
            onCreateTicket={handleCreateTicket}
          />
        )}
        {activeTab === 'tickets' && (
          <TicketPanel
            storage={storageAdapter}
            pushAdapter={pushAdapter}
            user={user}
            context={context}
            config={config}
            initialMessage={ticketInitialMessage}
          />
        )}
        {activeTab === 'agent' && isAgent && (
          <div className="-m-4"> {/* Remove parent padding for full-width agent panel */}
            <ChatAgentPanel
              storage={storageAdapter}
              pushAdapter={pushAdapter}
              presenceManager={presenceManager}
              user={user}
              config={config}
            />
          </div>
        )}
      </div>
    </div>
  );

  // ── Floating widget mode ──
  if (floating) {
    return (
      <>
        {/* Floating button */}
        {!isOpen && (
          <button
            onClick={toggleOpen}
            className="fixed bottom-6 right-6 w-14 h-14 rounded-full shadow-lg flex items-center justify-center text-white hover:opacity-90 transition-all hover:scale-105 z-50"
            style={{ backgroundColor: primaryColor }}
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
          </button>
        )}

        {/* Floating panel */}
        {isOpen && (
          <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-bottom-4 fade-in duration-200">
            {content}
          </div>
        )}
      </>
    );
  }

  // ── Inline mode ──
  return content;
}

// ── Tab Icons (inline SVG, no dependencies) ──
function QuestionIcon({ active }) {
  return (
    <svg className={`w-4 h-4 ${active ? 'text-stone-700' : 'text-stone-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <circle cx="12" cy="12" r="10" />
      <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" strokeLinecap="round" />
      <circle cx="12" cy="17" r="0.5" fill="currentColor" />
    </svg>
  );
}

function ChatBubbleIcon({ active }) {
  return (
    <svg className={`w-4 h-4 ${active ? 'text-stone-700' : 'text-stone-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function TicketIcon({ active }) {
  return (
    <svg className={`w-4 h-4 ${active ? 'text-stone-700' : 'text-stone-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 0 0-2 2v3a2 2 0 1 1 0 4v3a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-3a2 2 0 1 1 0-4V7a2 2 0 0 0-2-2H5z" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function DashboardIcon({ active }) {
  return (
    <svg className={`w-4 h-4 ${active ? 'text-stone-700' : 'text-stone-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

export default HelpCenter;
